import { useCallback } from 'react';

import { useIsPreview } from 'Shared/hooks/useIsPreview';
import { User } from 'Services/User';

import { FollowUserButton } from './FollowUserButton';
import { useUserFollowingButton } from './hooks/useUserFollowingButton';
import { useFollowUnfollowEffect } from './hooks/useFollowUnfollowEffect';

import type { CommonFollowUserButtonPropsT } from './types';
import type { FullArticleAuthorT } from 'Shared/types/ApiModels';
import type { AuthorT } from 'Services/api/authors/types';

type PropsT = Readonly<
  CommonFollowUserButtonPropsT & {
    readonly followingUserId: string;
    readonly author?: boolean;
    readonly onFollowToggle?: (arg1: boolean) => void;
    readonly user: FullArticleAuthorT | AuthorT;
  }
>;

export const FollowUserContainer = ({
  followingUserId,
  author = false,
  onFollowClick,
  onFollowToggle,
  user,
  ...restProps
}: PropsT) => {
  const onToggle = useCallback(() => {
    User.fetch();
  }, []);

  const { isLoading, isFollowing, toggleFollow } = useUserFollowingButton({
    followingUserId: String(followingUserId),
    isAuthor: author,
    slug: user.slug,
    onToggle,
  });

  const trackClick = useFollowUnfollowEffect(isFollowing, onFollowToggle);

  const handleClick = useCallback(
    // @ts-expect-error ImplicitAny
    (value) => {
      trackClick();
      onFollowClick?.(value);
    },
    [onFollowClick, trackClick],
  );

  const isPreview = useIsPreview();

  if (isPreview) {
    return null;
  }

  return (
    <FollowUserButton
      {...restProps}
      isFollowing={isFollowing}
      isLoading={isLoading}
      toggleFollow={toggleFollow}
      user={user}
      onFollowClick={handleClick}
    />
  );
};
